import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { EditorContent, type Editor } from "@tiptap/react";
import {
  Code,
  Type,
  ChevronDown,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Undo,
  Redo,
  Variable,
  Globe,
} from "lucide-react";
import clsx from "clsx";
import type { VariableInfo } from "./types";

interface TemplateEditorPanelProps {
  editor: Editor | null;
  editorMode: "wysiwyg" | "html";
  language: "cs" | "sk";
  content: string;
  contentSk: string;
  showVariables: boolean;
  variables: Record<string, VariableInfo[]>;
  onEditorModeChange: (mode: "wysiwyg" | "html") => void;
  onLanguageChange: (lang: "cs" | "sk") => void;
  onContentChange: (value: string) => void;
  onContentSkChange: (value: string) => void;
  onShowVariablesChange: (show: boolean) => void;
}

export default function TemplateEditorPanel({
  editor,
  editorMode,
  language,
  content,
  contentSk,
  showVariables,
  variables,
  onEditorModeChange,
  onLanguageChange,
  onContentChange,
  onContentSkChange,
  onShowVariablesChange,
}: TemplateEditorPanelProps) {
  const { t } = useTranslation();
  // Localized headings for the variable dropdown groups (group ids come from the API).
  const groupLabels: Record<string, string> = {
    customer: t("opsAdmin.templates.variableGroups.customer"),
    trip: t("opsAdmin.templates.variableGroups.trip"),
    order: t("opsAdmin.templates.variableGroups.order"),
    guide: t("opsAdmin.templates.variableGroups.guide"),
    company: t("opsAdmin.templates.variableGroups.company"),
    voucher: t("opsAdmin.templates.variableGroups.voucher"),
  };
  // Insert variable at cursor
  const insertVariable = useCallback(
    (variable: string) => {
      const tag = `{{${variable}}}`;
      if (editorMode === "wysiwyg" && editor) {
        editor.commands.insertContent(tag);
      } else {
        // For HTML mode, append to content
        if (language === "sk") {
          onContentSkChange(contentSk + tag);
        } else {
          onContentChange(content + tag);
        }
      }
      onShowVariablesChange(false);
    },
    [
      editor,
      editorMode,
      language,
      content,
      contentSk,
      onContentChange,
      onContentSkChange,
      onShowVariablesChange,
    ],
  );

  return (
    <div className="card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-4 dark:border-gray-700">
        {/* Mode toggle */}
        <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => onEditorModeChange("wysiwyg")}
            className={clsx(
              "flex items-center gap-1 rounded-l-lg px-3 py-1.5 text-sm transition-colors",
              editorMode === "wysiwyg"
                ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800",
            )}
          >
            <Type className="h-4 w-4" />
            WYSIWYG
          </button>
          <button
            onClick={() => onEditorModeChange("html")}
            className={clsx(
              "flex items-center gap-1 rounded-r-lg px-3 py-1.5 text-sm transition-colors",
              editorMode === "html"
                ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800",
            )}
          >
            <Code className="h-4 w-4" />
            HTML
          </button>
        </div>

        {/* Language toggle */}
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-gray-400" />
          <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => onLanguageChange("cs")}
              className={clsx(
                "rounded-l-lg px-3 py-1.5 text-sm transition-colors",
                language === "cs"
                  ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                  : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800",
              )}
            >
              EN
            </button>
            <button
              onClick={() => onLanguageChange("sk")}
              className={clsx(
                "rounded-r-lg px-3 py-1.5 text-sm transition-colors",
                language === "sk"
                  ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                  : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800",
              )}
            >
              SK
            </button>
          </div>
        </div>

        {/* Variables dropdown */}
        <div className="relative">
          <button
            onClick={() => onShowVariablesChange(!showVariables)}
            className="btn-ghost btn-sm"
          >
            <Variable className="h-4 w-4" />
            {t("opsAdmin.templates.variables")}
            <ChevronDown className="h-4 w-4" />
          </button>

          {showVariables && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => onShowVariablesChange(false)} />
              <div className="absolute right-0 z-50 mt-2 max-h-96 w-72 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                {Object.entries(variables).map(([group, vars]) => (
                  <div key={group}>
                    <div className="sticky top-0 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                      {groupLabels[group] ?? group}
                    </div>
                    {vars.map((v) => (
                      <button
                        key={v.key}
                        onClick={() => insertVariable(v.key)}
                        className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <span className="text-primary-600 font-mono">{`{{${v.key}}}`}</span>
                        <span className="ml-2 text-gray-500">{v.label}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* WYSIWYG toolbar */}
      {editorMode === "wysiwyg" && editor && (
        <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-200 pb-4 dark:border-gray-700">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={clsx(
              "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
              editor.isActive("bold") && "bg-gray-200 dark:bg-gray-700",
            )}
          >
            <Bold className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={clsx(
              "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
              editor.isActive("italic") && "bg-gray-200 dark:bg-gray-700",
            )}
          >
            <Italic className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={clsx(
              "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
              editor.isActive("underline") && "bg-gray-200 dark:bg-gray-700",
            )}
          >
            <UnderlineIcon className="h-4 w-4" />
          </button>
          <div className="mx-2 w-px bg-gray-200 dark:bg-gray-700" />
          <button
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            className={clsx(
              "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
              editor.isActive({ textAlign: "left" }) && "bg-gray-200 dark:bg-gray-700",
            )}
          >
            <AlignLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            className={clsx(
              "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
              editor.isActive({ textAlign: "center" }) && "bg-gray-200 dark:bg-gray-700",
            )}
          >
            <AlignCenter className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            className={clsx(
              "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
              editor.isActive({ textAlign: "right" }) && "bg-gray-200 dark:bg-gray-700",
            )}
          >
            <AlignRight className="h-4 w-4" />
          </button>
          <div className="mx-2 w-px bg-gray-200 dark:bg-gray-700" />
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={clsx(
              "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
              editor.isActive("bulletList") && "bg-gray-200 dark:bg-gray-700",
            )}
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={clsx(
              "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
              editor.isActive("orderedList") && "bg-gray-200 dark:bg-gray-700",
            )}
          >
            <ListOrdered className="h-4 w-4" />
          </button>
          <div className="mx-2 w-px bg-gray-200 dark:bg-gray-700" />
          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="rounded p-2 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
          >
            <Undo className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="rounded p-2 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
          >
            <Redo className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Editor content */}
      {editorMode === "wysiwyg" ? (
        <EditorContent
          editor={editor}
          className="prose prose-sm dark:prose-invert focus-within:border-primary-500 focus-within:ring-primary-500/20 min-h-[400px] max-w-none rounded-lg border border-gray-200 p-4 focus-within:ring-2 dark:border-gray-700"
        />
      ) : (
        <textarea
          value={language === "sk" ? contentSk : content}
          onChange={(e) =>
            language === "sk" ? onContentSkChange(e.target.value) : onContentChange(e.target.value)
          }
          placeholder="<html>...</html>"
          className="input min-h-[400px] font-mono text-sm"
        />
      )}
    </div>
  );
}
